import type { AspectRatio, OutputSizeTier } from '@studio/contract';

/**
 * Standard numeric aspect ratios used for mismatch detection.
 * Source: docs/research/legacy-photo-studio-architecture.md §"STANDARD_RATIOS".
 */
export const STANDARD_RATIOS = [21 / 9, 16 / 9, 3 / 2, 4 / 3, 1, 3 / 4, 2 / 3, 9 / 16, 9 / 21];

const ASPECT_MISMATCH_THRESHOLD = 0.15;
const LANDSCAPE_THRESHOLD = 1.15;
const PORTRAIT_THRESHOLD = 0.87;

/** Base long-side pixel dimension per size tier, rounded to 32px (BFL Flux grid). */
const TIER_BASE_PX: Record<OutputSizeTier, number> = {
  standard: 1024,
  large: 1440,
  xl: 2048,
};

/** Parse "16:9" → 16/9. Returns 1 for unrecognised inputs. */
function parseRatioNum(ratio: string): number {
  const parts = ratio.split(':').map(Number);
  const w = parts[0];
  const h = parts[1];
  if (!w || !h) return 1;
  return w / h;
}

/**
 * Compute output pixel dimensions for a given aspect ratio and size tier.
 * Returns null for 'original' and 'template' (those defer to the source image).
 * Dimensions are rounded to the nearest 32px (BFL Flux input grid).
 *
 * Source: docs/research/legacy-photo-studio-architecture.md §"calculateOutputDimensions".
 */
export function calculateOutputDimensions(
  aspectRatio: AspectRatio,
  sizeTier: OutputSizeTier,
): { w: number; h: number } | null {
  if (aspectRatio === 'original' || aspectRatio === 'template') return null;

  const base = TIER_BASE_PX[sizeTier];
  const ratio = parseRatioNum(aspectRatio);

  let w: number;
  let h: number;

  if (ratio >= 1) {
    // Landscape or square: fix width to base, derive height.
    w = base;
    h = Math.round(base / ratio / 32) * 32;
  } else {
    // Portrait: fix height to base, derive width.
    h = base;
    w = Math.round((base * ratio) / 32) * 32;
  }

  return { w: Math.max(w, 32), h: Math.max(h, 32) };
}

/**
 * Detect aspect-ratio mismatch to drive the 'warning' credits-chip skin.
 *
 * Case A — aspectRatio 'original' or 'template' and srcW/srcH is >15% off
 *   every standard ratio → 'unusual' warning.
 * Case B — non-original/template output would flip orientation vs source
 *   (landscape source → portrait output or vice versa) → 'orientation' warning.
 *
 * Returns null when srcW/srcH is unknown or no mismatch is detected.
 * Source: docs/research/legacy-photo-studio-architecture.md §"Aspect-ratio mismatch".
 */
export function detectAspectMismatch(
  aspectRatio: AspectRatio,
  srcW?: number,
  srcH?: number,
): 'unusual' | 'orientation' | null {
  if (!srcW || !srcH) return null;
  const srcRatio = srcW / srcH;

  if (aspectRatio === 'original' || aspectRatio === 'template') {
    // Case A: source ratio is unusual if it's >15% off every standard ratio.
    const isUnusual = STANDARD_RATIOS.every(
      (std) => Math.abs(srcRatio - std) / std > ASPECT_MISMATCH_THRESHOLD,
    );
    return isUnusual ? 'unusual' : null;
  }

  // Case B: orientation flip between source and target.
  const targetRatio = parseRatioNum(aspectRatio);
  const srcIsLandscape = srcRatio > LANDSCAPE_THRESHOLD;
  const srcIsPortrait = srcRatio < PORTRAIT_THRESHOLD;
  const targetIsPortrait = targetRatio < PORTRAIT_THRESHOLD;
  const targetIsLandscape = targetRatio > LANDSCAPE_THRESHOLD;

  if ((srcIsLandscape && targetIsPortrait) || (srcIsPortrait && targetIsLandscape)) {
    return 'orientation';
  }

  return null;
}
